// Honest states (DESIGN-SYSTEM §00 over §10.16, SPEC §22): empty (a quiet fill, one line + one action), dependency / awaiting MoET (a
// dashed outline, plug icon, an action), error (red tint + Retry), warning, information / no access (lock), and body-shaped static
// skeletons (no spinner, no shimmer: §00.10). Gaps are never
// filled with invented data; every state names what is missing and what a person can do. WP-THEME.
import { CircleAlert, Hourglass, Info, Lock, Plug, TriangleAlert } from "lucide-react";
import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { cx } from "./lang";

export type NoticeKind = "empty" | "dependency" | "awaiting" | "error" | "warning" | "info" | "no-access";

export interface NoticeProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  kind: NoticeKind;
  title?: ReactNode;
  /** One sentence: what is missing, or what happened. */
  children?: ReactNode;
  /** One action (a link or a button), e.g. "Request data", "Retry", "Open the Knowledge Bank". */
  action?: ReactNode;
  /** Replaces the default glyph. */
  icon?: ReactNode;
}

const CLASS: Record<NoticeKind, string> = {
  empty: "ec-empty",
  dependency: "ec-dep",
  awaiting: "ec-dep",
  error: "ec-errbox",
  warning: "ec-warnbox",
  info: "ec-infobox",
  "no-access": "ec-infobox",
};

const GLYPH: Partial<Record<NoticeKind, typeof Plug>> = {
  dependency: Plug,
  awaiting: Hourglass,
  error: CircleAlert,
  warning: TriangleAlert,
  info: Info,
  "no-access": Lock,
};

export function Notice({ kind, title, children, action, icon, className, ...rest }: NoticeProps) {
  const Glyph = GLYPH[kind];
  const glyph = icon ?? (Glyph ? <Glyph aria-hidden strokeWidth={1.5} /> : null);
  const role = kind === "error" ? "alert" : undefined;
  const stacked = kind === "empty" || kind === "dependency" || kind === "awaiting";
  return (
    <div className={cx(CLASS[kind], className)} role={role} data-state={kind} {...rest}>
      {glyph}
      <div className="min-w-0 flex-1">
        {title && <div className={stacked ? "t" : "font-semibold"}>{title}</div>}
        {children && <div className={stacked ? "s" : undefined}>{children}</div>}
        {action && <div className={stacked ? "mt-1" : "mt-1.5"}>{action}</div>}
      </div>
    </div>
  );
}

export interface SkeletonBlockProps extends HTMLAttributes<HTMLDivElement> {
  width?: number | string;
  height?: number | string;
}

/** A static bone in the shape of the content it stands for (no shimmer on content, §00.10). */
export function SkeletonBlock({ width = "100%", height = 12, className, style, ...rest }: SkeletonBlockProps) {
  const s: CSSProperties = { width, height, ...style };
  return <div aria-hidden className={cx("ec-skel", className)} style={s} {...rest} />;
}
